import type { ButtonHTMLAttributes, ComponentType, SVGProps } from "react";
import { forwardRef } from "react";
import { cn } from "../../lib/utils";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

export type IconButtonVariant = "ghost" | "secondary" | "primary";
export type IconButtonSize = "sm" | "md" | "lg";

const VARIANT: Record<IconButtonVariant, string> = {
  ghost: "text-ink-muted hover:bg-black/[0.05] hover:text-ink",
  secondary:
    "border border-black/10 bg-white text-ink-soft shadow-card hover:border-primary/40 hover:text-primary",
  primary: "bg-primary text-white shadow-card hover:bg-primary-hover",
};

const SIZE: Record<IconButtonSize, string> = {
  sm: "size-7",
  md: "size-9",
  lg: "size-10",
};

const ICON: Record<IconButtonSize, string> = {
  sm: "size-4",
  md: "size-[18px]",
  lg: "size-5",
};

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon: IconType;
  /** Required: icon-only controls need an accessible name. */
  "aria-label": string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    {
      icon: Icon,
      variant = "ghost",
      size = "md",
      className,
      type = "button",
      ...props
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex shrink-0 items-center justify-center rounded-full transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
          VARIANT[variant],
          SIZE[size],
          className
        )}
        {...props}
      >
        <Icon aria-hidden="true" className={ICON[size]} />
      </button>
    );
  }
);
